import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { toast } from "react-toastify";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

/** OpenStreetMap-derived data via Komoot’s public Photon API (no API key; use fairly). */
const PHOTON_SEARCH = "https://photon.komoot.io/api/";
const PHOTON_REVERSE = "https://photon.komoot.io/reverse";

const DEFAULT_CENTER = [24.7136, 46.6753];
const DEFAULT_ZOOM = 11;
const PIN_ZOOM = 15;
const SEARCH_DEBOUNCE_MS = 380;

function photonFeatureLabel(feature) {
  const p = feature?.properties || {};
  const streetLine = [p.housenumber, p.street].filter(Boolean).join(" ").trim();
  const locality = p.city || p.town || p.village || p.district || p.county || "";
  const parts = [];
  if (p.name) parts.push(p.name);
  if (streetLine) parts.push(streetLine);
  if (locality) parts.push(locality);
  if (p.state) parts.push(p.state);
  if (p.country) parts.push(p.country);
  const joined = parts.filter(Boolean).join(", ");
  return joined || p.name || streetLine || locality || "Selected place";
}

async function photonSearch(query) {
  const url = `${PHOTON_SEARCH}?q=${encodeURIComponent(query)}&limit=8&lang=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Search failed");
  const data = await res.json();
  return Array.isArray(data.features) ? data.features : [];
}

async function photonReverseLabel(lat, lng) {
  const url = `${PHOTON_REVERSE}?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&lang=en`;
  const res = await fetch(url);
  if (!res.ok) return "";
  const data = await res.json();
  const f = data.features?.[0];
  return f ? photonFeatureLabel(f) : "";
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapRecenter({ lat, lng, hasPin }) {
  const map = useMap();
  useEffect(() => {
    if (hasPin && Number.isFinite(lat) && Number.isFinite(lng)) {
      map.setView([lat, lng], PIN_ZOOM);
    }
  }, [lat, lng, hasPin, map]);
  return null;
}

export default function CheckoutMap({ latitude, longitude, onMapLocationUpdate, labels }) {
  const searchWrapRef = useRef(null);
  const searchTimerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const latNum = Number.parseFloat(String(latitude || "").replace(",", "."));
  const lngNum = Number.parseFloat(String(longitude || "").replace(",", "."));
  const hasPin = Number.isFinite(latNum) && Number.isFinite(lngNum);

  const center = useMemo(
    () => (hasPin ? [latNum, lngNum] : DEFAULT_CENTER),
    [hasPin, latNum, lngNum]
  );

  const applyPosition = useCallback(
    async (lat, lng, addressLabel) => {
      const latN = Number(lat);
      const lngN = Number(lng);
      if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return;
      let label = typeof addressLabel === "string" ? addressLabel : "";
      if (!label.trim()) {
        try {
          label = await photonReverseLabel(latN, lngN);
        } catch {
          label = "";
        }
      }
      onMapLocationUpdate(
        String(Math.round(latN * 1e6) / 1e6),
        String(Math.round(lngN * 1e6) / 1e6),
        label.trim() || undefined
      );
    },
    [onMapLocationUpdate]
  );

  const onMapPick = useCallback(
    (lat, lng) => {
      applyPosition(lat, lng, "");
    },
    [applyPosition]
  );

  useEffect(() => {
    function onDocDown(e) {
      const el = searchWrapRef.current;
      if (!el || el.contains(e.target)) return;
      setSearchOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = searchQuery.trim();
    if (q.length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const feats = await photonSearch(q);
        setSearchResults(feats);
        setSearchOpen(true);
      } catch {
        toast.error("Address search unavailable. Try again or pick on the map.");
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  const pickFeature = useCallback(
    (feature) => {
      const coords = feature?.geometry?.coordinates;
      if (!coords || coords.length < 2) return;
      const [lon, lat] = coords;
      const label = photonFeatureLabel(feature);
      setSearchQuery(label);
      setSearchResults([]);
      setSearchOpen(false);
      applyPosition(lat, lon, label);
    },
    [applyPosition]
  );

  const onUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Your browser does not support location.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          await applyPosition(lat, lng, "");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied."
            : "Could not read your location.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 }
    );
  }, [applyPosition]);

  const title = labels?.title ?? "Delivery location on map";
  const hint =
    labels?.hint ??
    "Search (OpenStreetMap data), use My location, or click the map. Address fills when we find a label for the pin.";
  const pinHint =
    labels?.pinHint ?? "Coordinates are included with your order.";

  return (
    <div className="store-checkout-map-wrap">
      <span className="store-form-label store-checkout-map-label">{title}</span>
      <p className="store-checkout-map-hint">{hint}</p>

      <div className="store-checkout-map-toolbar">
        <div className="store-checkout-map-search-wrap" ref={searchWrapRef}>
          <input
            type="text"
            className="store-checkout-map-search-input"
            placeholder="Search address or place…"
            autoComplete="off"
            aria-label="Search address on map"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value.trim().length >= 3) setSearchOpen(true);
            }}
            onFocus={() => {
              if (searchResults.length > 0) setSearchOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearchOpen(false);
            }}
          />
          {searchOpen && searchQuery.trim().length >= 3 ? (
            <ul
              id="checkout-map-search-results"
              className="store-checkout-map-search-results"
              role="listbox"
            >
              {searchLoading ? (
                <li className="store-checkout-map-search-item store-checkout-map-search-item--muted">
                  Searching…
                </li>
              ) : searchResults.length === 0 ? (
                <li className="store-checkout-map-search-item store-checkout-map-search-item--muted">
                  No results — try another phrase or tap the map.
                </li>
              ) : (
                searchResults.map((f, idx) => {
                  const p = f.properties || {};
                  const key = `${p.osm_id ?? "x"}-${p.osm_type ?? "x"}-${idx}`;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        className="store-checkout-map-search-hit"
                        role="option"
                        aria-selected={false}
                        onClick={() => pickFeature(f)}
                      >
                        {photonFeatureLabel(f)}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          className="store-checkout-map-locate-btn"
          onClick={onUseMyLocation}
          disabled={geoLoading}
          aria-busy={geoLoading}
        >
          {geoLoading ? "Locating…" : "My location"}
        </button>
      </div>

      <div className="store-checkout-map" role="application" aria-label={title}>
        <MapContainer
          center={center}
          zoom={hasPin ? PIN_ZOOM : DEFAULT_ZOOM}
          className="store-checkout-map-inner"
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapRecenter lat={latNum} lng={lngNum} hasPin={hasPin} />
          <MapClickHandler onMapClick={onMapPick} />
          {hasPin ? (
            <Marker
              position={[latNum, lngNum]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const p = e.target.getLatLng();
                  onMapPick(p.lat, p.lng);
                },
              }}
            />
          ) : null}
        </MapContainer>
      </div>

      {hasPin ? (
        <p className="store-checkout-map-coords" aria-live="polite">
          {pinHint}{" "}
          <span className="store-checkout-map-coords-value">
            {latNum.toFixed(5)}, {lngNum.toFixed(5)}
          </span>
        </p>
      ) : null}
    </div>
  );
}
